<template>
    <div
    class="menu-main-item-wrapper"
    :class="{
        'menu-collapsed': menuCollapsed,
        'selected': selected,
        'open': showingSubItems,
        'sub-item-selected': subItemSelected && (!showingSubItems || menuCollapsed),
        'mobile': getMobileAppState
    }"
    @click="emit('clicked', menuEntry)">
        <!-- Icon -->
        <app-icon v-if="menuEntry.icon" class="icon"/>
        <!-- Text for menu entry -->
        <transition name="appear">
            <div v-if="!menuCollapsed" class="menu-item-label">{{menuEntry.name}}</div>
        </transition>
        <!-- Expand/Close Icon -->
        <icon-arrow-down v-if="!menuEntry.terminal && menuEntry.children?.length && !menuCollapsed" class="open-icon" :class="{ 'open': showingSubItems }"/>
    </div>
</template>

<script setup>
import IconArrowDown from '@/assets/icons/arrow-down.svg?inline'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@menuEntry {Object} with menu info, tho it has many info, the only one needed is:
- icon {String}
- name {String}
@menuCollapsed {Boolean}
@selected {String}
@showingSubItems {Boolean}
@subItemSelected {Boolean}
**/
const props = defineProps({
    menuEntry: Object,
    menuCollapsed: Boolean,
	selected: Boolean,
	showingSubItems: Boolean,
    subItemSelected: Boolean
})

const { getMobileAppState } = mapGetters()

/* Computed */
const appIcon = computed(() => {
	return defineAsyncComponent(() => import('@/assets/icons/' + props.menuEntry.icon + '.svg?inline'))
})
</script>

<style lang="scss" scoped>
.menu-main-item-wrapper {
    width: 100%;
    height: 48px;
    padding: 0 16px;

    display: flex;
    align-items: center;

    border-radius: 8px;
    cursor: pointer;

    .menu-item-label {
        width: 100%;
        height: 48px;

        @include H7-Medium;
        line-height: 48px;
        color: var(--menu-main-text-icon-color);

        opacity: 1;

        &.appear-enter-active,
        &.appear-leave-active {
            opacity: 1;
            transition: all 0.4s ease-in-out;
        }

        &.appear-enter-from,
        &.appear-leave-to {
            opacity: 0.2;
            transition: all 0.4s ease-in-out;
        }
    }

    .icon, .open-icon {
        width: 16px;
        height: 16px;
        transition: all .4s ease;
        flex-shrink: 0;

        :deep(.change-color-fill) {
            fill: var(--menu-main-text-icon-color);
        }
    }

    .icon {
        margin-right: 8px;
    }

    .open-icon {
        margin-left: 8px;
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: rgba(var(--menu-main-bg-color-hover), var(--menu-bg-opacity-state-hover));
        }
    }

    &:active {
        background-color: rgba(var(--menu-main-bg-color-active), var(--menu-bg-opacity-state-active));
    }

    &.open {
        margin-bottom: 1px;
		background-color: rgba(var(--menu-main-bg-color-open), var(--menu-bg-opacity-state-open));
        border-radius: 8px 8px 0 0;

        .open-icon {
            transform: rotate(-180deg);
        }

        &.menu-collapsed {
            border-radius: 8px;
            background-color: transparent;
            transition: background-color 0.4s ease-in-out;

            &.sub-item-selected {
                background-color: var(--menu-main-bg-selected);

                .icon, .open-icon {
                    :deep(.change-color-fill) {
                        fill: var(--menu-main-text-icon-selected);
                    }
                }
            }
        }
    }

    &.selected, &.sub-item-selected {
        background-color: var(--menu-main-bg-selected);

        .menu-item-label {
            color: var(--menu-main-text-icon-selected);
        }

        .icon, .open-icon {
            :deep(.change-color-fill) {
                fill: var(--menu-main-text-icon-selected);
            }
        }
    }

    &.mobile {
        background-color: var(--menu-main-base-color);
        border-radius: 0;

        .menu-item-label {
            @include H6-Medium;
            color: var(--secondary-dark-2);
        }

        .icon {
            :deep(.change-color-fill) {
                fill: var(--primary);
            }
        }

        .open-icon {
            :deep(.change-color-fill) {
                fill: var(--secondary);
            }
        }

        &:active {
            background-color: var(--secondary-light-1);
        }

        &.open {
            margin-bottom: 0;
            border-bottom: 1px solid rgba(var(--secondary-dark-4-rgb), 0.08);
            background-color: var(--secondary-light-3);

            .open-icon {
                :deep(.change-color-fill) {
                    fill: var(--primary);
                }
            }
        }
    }
}
</style>
